import { useState, useEffect, useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { getStates, getAdressTypeOption, getPreferredContact } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import usePrevValue from "hooks/usePrevValue";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import ScheduleAddress from "pages/shipper/OrderConsolidation/components/ShipmentDetails/components/Schedule/ScheduleAddress";
import AddressForm from "./AddressForm";
import AddressTypeForm from "./AddressTypeForm";
import PortCountry from "./PortCountry";
import PortForm from "./PortForm";
import { formatAddress } from "./helpers";
import FormItemInfo from "../../Common/FormItemInfo";
import useScheduleAddress from "../hooks/useScheduleAddress";
import { ICountryStates, IPickupDeliveryAddressProps } from "../Types";
import PickupDeliveryEditAddress from "../PickupDeliveryEditAddress";
import "../Schedule.scss";

function PickupDeliveryAddress(props: IPickupDeliveryAddressProps) {
  const {
    isPickupOrDelivery,
    isQuoteRequest,
    isShipment,
    formState,
    isOffPlatformShipment,
    contactBook,
    countries,
    getTimeZones,
  } = props || {};

  const { addressBook, setSearchPlace, getAddresses } = useScheduleAddress({
    typeKey: isPickupOrDelivery,
  });
  const [portAddressOption, setPortAddressOption] = useState([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editAddress, setEditAddress] = useState(false);
  const [countryStates, setCountryStates] = useState<ICountryStates[]>([]);
  const [search, setSearch] = useState("");
  const portRequestAbortController = useRef<AbortController | null>(null);
  const isInitialAddressBookMount = useRef(true);
  /** Timezone from the selected airport/seaport API `address` payload — used to restore when optional book address is cleared. */
  const portDerivedTimezoneRef = useRef<string | null>(null);
  const prevOptionalAddressNameRef = useRef<string | undefined>(undefined);
  const intl = useIntl();
  const methods = useFormContext();
  const {
    setValue,
    getValues,
    watch,
    trigger,
    control,
    clearErrors,
    formState: { dirtyFields },
  } = methods;

  const country = watch(`schedule.${isPickupOrDelivery}_country`);
  const countryCode = watch(`schedule.${isPickupOrDelivery}_country_code`);
  const isEditConsolidatedShipment = watch("shipment.shipment_type") === 2 || false;

  const { permissions }: any = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions)?.company_shipper_settings;
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const addressType = useWatch({ name: `schedule.${isPickupOrDelivery}_address_type`, control });
  const watchedOptionalAddressName = useWatch({ name: `schedule.${isPickupOrDelivery}_address_name`, control });
  const prevAddressType = usePrevValue(addressType);
  const isAddressTypeDirty = Boolean((dirtyFields as any)?.schedule?.[`${isPickupOrDelivery}_address_type`]);

  const registerPortLinkedTimezone = useCallback((tz: string | null) => {
    portDerivedTimezoneRef.current = tz;
  }, []);

  const invitedModes = useWatch({ name: "invited_modes", control });
  const customField1 = useWatch({ name: `organizational.custom_field_01`, control });
  const prevCustomField1 = usePrevValue(customField1);

  useEffect(() => {
    // debounce 300ms for airport and port search
    if (search) {
      const timeoutId = setTimeout(() => {
        handleAddressTypeOption();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [search]);

  // When the WEB-10382 port-linked address flow is active, clear any previously
  // selected address + address_pk / reference_number when the user switches
  // between address / airport / seaport. Values are repopulated from user input
  // or the next port selection (timezone from port; optional address from book).
  useEffect(() => {
    if (!isOptionalPortAddressFormEnabled || !enable_new_address_structure) return;
    if (!isAddressTypeDirty) return;
    if (prevAddressType === undefined || prevAddressType === addressType) return;
    const codeType = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
    setValue(`schedule.${codeType}_address_pk`, undefined);
    setValue(`schedule.${codeType}_address_reference_number`, "");
    setValue(`schedule.${isPickupOrDelivery}_address_name`, "");
    setValue(`schedule.${isPickupOrDelivery}_street`, "");
    setValue(`schedule.${isPickupOrDelivery}_street_line2`, "");
    setValue(`schedule.${isPickupOrDelivery}_city`, "");
    setValue(`schedule.${isPickupOrDelivery}_state`, "");
    setValue(`schedule.${isPickupOrDelivery}_country`, "");
    setValue(`schedule.${isPickupOrDelivery}_country_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_city`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_zipcode`, "");
    setValue(`schedule.${isPickupOrDelivery}_timezone`, "");
    setValue(`schedule.${isPickupOrDelivery}_un_locode`, "");
    setValue(`schedule.${isPickupOrDelivery}_location_reference_number`, "");
    setValue(`schedule.${isPickupOrDelivery}_custom_address_reference`, "");
    portDerivedTimezoneRef.current = null;
    prevOptionalAddressNameRef.current = undefined;
    clearErrors([
      `schedule.${isPickupOrDelivery}_address_name`,
      `schedule.${isPickupOrDelivery}_street`,
      `schedule.${isPickupOrDelivery}_street_line2`,
      `schedule.${isPickupOrDelivery}_city`,
      `schedule.${isPickupOrDelivery}_state`,
      `schedule.${isPickupOrDelivery}_country`,
      `schedule.${isPickupOrDelivery}_country_code`,
      `schedule.${isPickupOrDelivery}_zipcode`,
      `schedule.${isPickupOrDelivery}_timezone`,
    ]);
  }, [
    addressType,
    prevAddressType,
    isOptionalPortAddressFormEnabled,
    enable_new_address_structure,
    isAddressTypeDirty,
  ]);

  // Optional address cleared → restore schedule timezone to the port-linked value (airport/seaport only).
  useEffect(() => {
    const current = watchedOptionalAddressName ?? "";
    const prev = prevOptionalAddressNameRef.current;
    prevOptionalAddressNameRef.current = current;

    if (!isOptionalPortAddressFormEnabled || !enable_new_address_structure) return;
    if (addressType !== "airport" && addressType !== "seaport") return;

    const clearedBookAddress = prev !== undefined && prev !== "" && current === "";
    if (!clearedBookAddress) return;

    const hasPort =
      getValues(`schedule.${isPickupOrDelivery}_airport_code`) ||
      getValues(`schedule.${isPickupOrDelivery}_seaport_code`) ||
      getValues(`schedule.${isPickupOrDelivery}_airport`) ||
      getValues(`schedule.${isPickupOrDelivery}_seaport`);

    const tz = portDerivedTimezoneRef.current;
    if (!hasPort || !tz) return;

    setValue(`schedule.${isPickupOrDelivery}_timezone`, tz, { shouldValidate: true });
    getTimeZones(true, "", tz);
  }, [
    watchedOptionalAddressName,
    addressType,
    enable_new_address_structure,
    isOptionalPortAddressFormEnabled,
    isPickupOrDelivery,
    setValue,
    getTimeZones,
    getValues,
  ]);

  useEffect(() => {
    const countryValue = countryCode || country;
    if (countryValue) checkCountryForStates(countryValue);
  }, [countryCode, country]);

  useEffect(() => {
    trigger(`schedule.${isPickupOrDelivery}_zipcode`);
    trigger(`schedule.${isPickupOrDelivery}_city`);
    trigger(`schedule.${isPickupOrDelivery}_street`);
    trigger(`schedule.${isPickupOrDelivery}_street_line2`);
    trigger(`schedule.${isPickupOrDelivery}_country`);
    if (countryStates?.length > 0 && getValues(`schedule.${isPickupOrDelivery}_country`)) {
      trigger(`schedule.${isPickupOrDelivery}_state`);
    }
  }, [invitedModes, editAddress]);

  /**
   * if value of customField1 changes from dropdown
   * check if pickup or delivery address is available and prefill it.
   */
  useEffect(() => {
    if (customField1 && prevCustomField1 !== customField1) {
      const customField1Options = formState.locationDetails?.organizational_options?.custom_field_01;
      if (customField1Options?.dropdown_values && Array.isArray(customField1Options.dropdown_values)) {
        const selectedCustomField1Option = customField1Options.dropdown_values.find(
          (item: any) => item.value === customField1,
        );
        const keyPrefix = isPickupOrDelivery === "pickup" ? "pickup" : "delivery";
        if (selectedCustomField1Option && selectedCustomField1Option[`${keyPrefix}_address`]) {
          const addresses = formatAddress(selectedCustomField1Option[`${keyPrefix}_address`]);
          autoFillAddress([addresses]);
        }
      }
    }
  }, [customField1, prevCustomField1]);

  const handleAddressTypeOption = () => {
    if (getValues(`schedule.${isPickupOrDelivery}_address_type`) !== "address") {
      if (portRequestAbortController.current) {
        portRequestAbortController.current.abort();
      }
      portRequestAbortController.current = new AbortController();
      const endpoint =
        getValues(`schedule.${isPickupOrDelivery}_address_type`) === "seaport" ? "/common/seaport" : "/common/airport";
      getAdressTypeOption(endpoint, search, { signal: portRequestAbortController.current?.signal })
        .then((res) => {
          setPortAddressOption(res.data.results);
        })
        .catch((error) => {
          if (error?.name === "CanceledError") {
            return;
          }
          errorToast(error);
        });
    }
  };

  const checkCountryForStates = (country: string, stateCode = null) => {
    if (enable_new_address_structure) {
      return;
    }
    getStates({
      params: {
        country_code: country,
      },
    })
      .then((res) => {
        const states = res.data.map(({ value, name, code }) => ({
          label: name,
          code,
          value,
        }));
        setCountryStates(states);
        const state = states?.length > 0 ? states.filter((state: ICountryStates) => state?.value === stateCode) : "";
        if (getValues(`schedule.${isPickupOrDelivery}_state`)) return;
        if (!getValues(`schedule.${isPickupOrDelivery}_state`) && state) {
          setValue(`schedule.${isPickupOrDelivery}_state`, state[0]?.label);
        }
        // if selected state value is not available in newly fetched options (when country changed)
        else if (
          !states.find(
            (state: ICountryStates) =>
              state?.label?.toLowerCase() === getValues(`schedule.${isPickupOrDelivery}_state`)?.toLowerCase(),
          )
        ) {
          setValue(`schedule.${isPickupOrDelivery}_state`, "");
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.schedule.couldCheckCountryOrState",
            defaultMessage: "Could not check country for states. Please try again later.",
          }),
        ),
      );
  };

  // clear address when address book is updated on location change (but not on initial load)
  useEffect(() => {
    if (isInitialAddressBookMount.current) {
      isInitialAddressBookMount.current = false;
      return;
    }

    // clear search place when address book is empty
    if (addressBook?.length === 0) {
      setSearchPlace("");
    }
  }, [addressBook]);

  const autoFillAddress = useCallback(
    (event) => {
      const address = event[0];

      setValue(`schedule.${isPickupOrDelivery}_zipcode`, "", { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_state`, "", { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_country`, "", { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_city`, "", { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_custom_address_reference`, "", { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_location_reference_number`, "", { shouldValidate: true });
      if (address) {
        if (enable_new_address_structure && !address?.reference_number) {
          return;
        }
        if (enable_new_address_structure) {
          const code_type = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
          setValue(`schedule.${code_type}_address_reference_number`, address.reference_number, {
            shouldValidate: true,
          });
        }
        setValue(`schedule.${isPickupOrDelivery}_address_name`, address.label || address.name, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_zipcode`, address.zipcode, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_street`, address.street_address, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_street_line2`, address.street_address_line_two);
        setValue(`schedule.${isPickupOrDelivery}_city`, address.city_name, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_country`, address.country_name, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_country_code`, address.country_code, { shouldValidate: true });
        setValue(`schedule.${isPickupOrDelivery}_timezone`, address.timezone, { shouldValidate: true });
        if (address?.state_name) {
          setValue(`schedule.${isPickupOrDelivery}_state`, address.state_name, { shouldValidate: true });
        }
        if (address?.custom_address_reference) {
          setValue(`schedule.${isPickupOrDelivery}_custom_address_reference`, address.custom_address_reference, {
            shouldValidate: true,
          });
        }
        if (address?.location_reference_number) {
          setValue(`schedule.${isPickupOrDelivery}_location_reference_number`, address.location_reference_number, {
            shouldValidate: true,
          });
        }
        if (address?.un_locode) {
          setValue(`schedule.${isPickupOrDelivery}_un_locode`, address.un_locode, {
            shouldValidate: true,
          });
        }

        // Fetch preferred contact for this address and fill the contact fields
        getPreferredContact(address?.reference_number).then((res) => {
          setValue(`schedule.${isPickupOrDelivery}_contact_name`, res.data.name, { shouldValidate: true });
          setValue(`schedule.${isPickupOrDelivery}_contact_phone`, res.data.phone_number, {
            shouldValidate: true,
          });
          setValue(`schedule.${isPickupOrDelivery}_contact_email`, res.data.email, { shouldValidate: true });
        });

        if (
          !isOffPlatformShipment &&
          (getValues(`schedule.${isPickupOrDelivery}_street`) === "" ||
            getValues(`schedule.${isPickupOrDelivery}_city`) === "" ||
            getValues(`schedule.${isPickupOrDelivery}_country`) === "" ||
            getValues(`schedule.${isPickupOrDelivery}_zipcode`) === "")
        ) {
          setEditAddress(true);
        }
        if (
          isOffPlatformShipment &&
          getValues(`schedule.${isPickupOrDelivery}_country`) === "" &&
          getValues(`schedule.${isPickupOrDelivery}_city`) === ""
        ) {
          setEditAddress(true);
        }
      }

      if (
        countryStates?.length > 0 &&
        getValues(`schedule.${isPickupOrDelivery}_country`) &&
        getValues(`schedule.${isPickupOrDelivery}_state`) === ""
      ) {
        setEditAddress(true);
      }
      if (!enable_new_address_structure) {
        setSearchPlace("");
      }
    },
    [addressBook, contactBook, countryStates],
  );

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <div className="w-200 mr-xl">
        <FormItemInfo
          labelId={`${
            isPickupOrDelivery === "pickup" ? "new-request.schedule.pickupAddress" : "new-request.schedule.deliveryAddress"
          }`}
          label={`${isPickupOrDelivery === "pickup" ? "Pickup address" : "Delivery address"}`}
          descId={`${
            isPickupOrDelivery === "pickup"
              ? "new-request.schedule.pickupLocationInfo"
              : "new-request.schedule.deliveryLocationInfo"
          }`}
          description={`${
            isPickupOrDelivery === "pickup" ? "Provide your pickup location info" : "Provide your delivery location info"
          }`}
        />
      </div>

      <div>
        <div className={enable_new_address_structure ? "" : "d-flex"}>
          <div>
            {(isQuoteRequest || isShipment) && !isEditConsolidatedShipment && (
              <div>
                <AddressTypeForm
                  isPickupOrDelivery={isPickupOrDelivery}
                  setPortAddressOption={setPortAddressOption}
                  formState={formState}
                />
              </div>
            )}

            {getValues(`schedule.${isPickupOrDelivery}_address_type`) !== "address" &&
              (isQuoteRequest || isShipment) &&
              !isEditConsolidatedShipment && (
                <div className="d-flex flex-column gap-xl mb-xl">
                  <PortForm
                    isPickupOrDelivery={isPickupOrDelivery}
                    formState={formState}
                    portAddressOption={portAddressOption}
                    getTimeZones={getTimeZones}
                    setSearch={setSearch}
                    registerPortLinkedTimezone={registerPortLinkedTimezone}
                  />
                  <PortCountry isPickupOrDelivery={isPickupOrDelivery} />
                </div>
              )}
          </div>

          {!isEditConsolidatedShipment && (
            <div className={`mb-xl ${enable_new_address_structure ? "width-sm" : "width-xs"}`}>
              <AddressForm
                isPickupOrDelivery={isPickupOrDelivery}
                formState={formState}
                addressBook={addressBook}
                setSearchPlace={setSearchPlace}
                setEditAddress={setEditAddress}
                autoFillAddress={autoFillAddress}
                getAddresses={getAddresses}
              />
            </div>
          )}
        </div>

        {!enable_new_address_structure && (
          <PickupDeliveryEditAddress
            isPickupOrDelivery={isPickupOrDelivery}
            isQuoteRequest={isQuoteRequest}
            isOffPlatformShipment={isOffPlatformShipment}
            countryStates={countryStates}
            formState={formState}
            countries={countries}
            isEditConsolidatedShipment={isEditConsolidatedShipment}
          />
        )}

        {isEditConsolidatedShipment && <ScheduleAddress isPickupOrDelivery={isPickupOrDelivery} showFormLabel={false} />}
      </div>
    </div>
  );
}

export default PickupDeliveryAddress;
